/* eslint-disable no-restricted-imports */
import ky, { type ResponsePromise } from 'ky';

import { createAuthInterceptor } from '~/api/interceptors/auth.interceptor';
import { bearerInterceptor } from '~/api/interceptors/bearer.interceptor';
import { errorInterceptor } from '~/api/interceptors/error.interceptor';
import { localeInterceptor } from '~/api/interceptors/locale.interceptor';
import { subsidiaryInterceptor } from '~/api/interceptors/subsidiary.interceptor';
import { mcpApiBaseUrl } from '~/constants/config/env';

/**
 * Global request timeout (ms). Bumped from Ky's 10s default to 120s because
 * a handful of curation + enrichment workflows take longer than the default
 * on cold path. Applied to every Ky instance below — keep them in sync by
 * sourcing from this constant rather than literal numbers.
 */
const API_TIMEOUT_MS = 120_000;

/**
 * Shared Ky config spread into every client below.
 *
 * `retry: 0` disables Ky's built-in retry app-wide. TanStack Query owns retry
 * policy for query-backed reads (see `src/api/queryClient.ts`); leaving Ky's
 * default retry on stacked a second, uncoordinated layer and turned one flaky
 * 5xx into up to ~9 requests (Ky's 3 attempts × Query's 3) plus a multi-second
 * error flash. Imperative (non-query) calls that still want retry opt back in
 * per-request via Ky's `retry` option (e.g. `downloadAnalyticsExperimentCsv`),
 * since those have no Query layer behind them.
 */
const baseKyConfig = {
  timeout: API_TIMEOUT_MS,
  retry: 0,
} as const;

/**
 * Dev:  Vite proxy intercepts /api and the /*-api aliases, rewrites to Cloud Run.
 * Prod: Firebase Hosting rewrites proxy to Cloud Run services (same-origin).
 */
const apiPrefixUrl = import.meta.env.DEV ? '/api' : '/api/v1/identity-service';
/**
 * Catalog-Operations service — platform-gateway hosts catalog-ops resources at
 * `/workflow/v1/enrichments/...`. Dev uses the proxy alias `/catalog-ops-api`;
 * prod relies on a Firebase Hosting rewrite for `/workflow/v1/enrichments/**`.
 */
const catalogOpsPrefixUrl = import.meta.env.DEV ? '/catalog-ops-api' : '/workflow/v1/enrichments';
/**
 * Curation service — platform-gateway hosts the per-batch product listings at
 * `/curation/runs/:curationRefId/products`. Dev uses the proxy alias
 * `/curation-api`; prod relies on a Firebase Hosting rewrite for
 * `/curation/**`. Distinct from catalog-ops because the URL prefix differs.
 */
const curationPrefixUrl = import.meta.env.DEV ? '/curation-api' : '/curation';
/**
 * Integrations service lives behind the platform-gateway Cloud Run service.
 * Dev routes through the Vite proxy alias `/integrations-api`; prod uses the
 * Firebase Hosting rewrite for `/api/v1/integration/**` → platform-gateway.
 * NOTE: singular `integration` — the gateway does not expose a plural alias.
 */
export const integrationsPrefixUrl = import.meta.env.DEV
  ? '/integrations-api'
  : '/api/v1/integration';
/**
 * Export service — read-only ClickHouse table browser, behind platform-gateway
 * at `/api/v1/export/**`. Dev routes through the Vite proxy alias `/export-api`;
 * prod uses the Firebase Hosting rewrite for `/api/v1/export/**` → platform-gateway.
 * Separate Cloud Run service (`export-api-service`) from integrations.
 */
const exportPrefixUrl = import.meta.env.DEV ? '/export-api' : '/api/v1/export';
/**
 * Experiment service — platform-gateway hosts experiment resources under
 * `/workflow/v1/experiments`, `/workflow/v1/experiments/:id`, etc. The Ky
 * prefix is `/workflow/v1` so `endpoints.experiments.*` can keep their
 * existing `experiments` / `experiments/${id}` paths (no `/api` segment,
 * unlike integration). Dev uses the proxy alias `/experiments-api`;
 * prod relies on a Firebase Hosting rewrite for `/workflow/v1/experiments/**`
 * → platform-gateway.
 */
const experimentsPrefixUrl = import.meta.env.DEV ? '/experiments-api' : '/workflow/v1';
/**
 * Catalog-Operations workflow service — hosts the unified trigger endpoint
 * (`/workflow/v1/catalog-operations/**`) that kicks off import/enrich/score/
 * deliver pipelines. Distinct Cloud Run route from `catalogOpsClient`, which
 * only covers the read-side `/workflow/v1/enrichments` jobs listing. Dev uses
 * the proxy alias `/catalog-operations-api`; prod relies on a Firebase
 * Hosting rewrite for `/workflow/v1/catalog-operations/**`.
 */
const catalogOperationsPrefixUrl = import.meta.env.DEV ? '/catalog-operations-api' : '/workflow/v1';
/**
 * Config service — shared platform-gateway config endpoints (KPIs, content
 * attributes). Dev uses `/config-api`; prod uses Firebase rewrite for
 * `/api/v1/config/**` → platform-gateway.
 */
const configPrefixUrl = import.meta.env.DEV ? '/config-api' : '/api/v1/config';
/**
 * Workflow execution-tracking service (`workflow-webapp`) — read + control API
 * for Temporal workflow runs (flow → coordinator → batch → sub-batch → product,
 * plus lineage), hosted by the platform-gateway at `/workflow/v1/workflow-runs`.
 * Shares the `/workflow/v1` prod prefix with catalog-ops/experiments but is a
 * DISTINCT Cloud Run resource, so it gets its own dev proxy alias
 * (`/workflow-runs-api`) and Firebase Hosting rewrite for
 * `/workflow/v1/workflow-runs/**`. Tenant is taken from the JWT `subsidiaryId`
 * claim (the subsidiary interceptor's `X-Subsidiary-ID` is an accepted override
 * only). Endpoints return data only where workers run with `TRACKING_ENABLED`.
 */
const workflowRunsPrefixUrl = import.meta.env.DEV ? '/workflow-runs-api' : '/workflow/v1';
/**
 * Analytics service — platform-gateway hosts the experiment-dashboard
 * read-side at `/api/v1/analytics/experiments/**`. Dev uses the proxy alias
 * `/analytics-api`; prod relies on a Firebase Hosting rewrite for
 * `/api/v1/analytics/**` → platform-gateway. Read-only; experiment CRUD
 * still flows through experimentsClient (workflow-service is the OLTP
 * source of truth, analytics is the aggregation layer).
 *
 * Exported so non-Ky callers (e.g. CSV download links that need an absolute
 * URL for `window.location.href`) share the same dev/prod URL switch as
 * `analyticsClient` instead of duplicating the ternary.
 */
export const analyticsPrefixUrl = import.meta.env.DEV ? '/analytics-api' : '/api/v1/analytics';
/**
 * Notification service — platform-gateway exposes channel configs, preferences,
 * templates, inbox, analytics under `/api/v1/notification/**` (singular). Dev
 * routes through the Vite proxy alias `/notifications-api`; prod relies on the
 * Firebase Hosting rewrite for `/api/v1/notification/**` → platform-gateway.
 */
const notificationsPrefixUrl = import.meta.env.DEV ? '/notifications-api' : '/api/v1/notification';
/**
 * Agent APIs — platform-gateway's wildcard agent proxy. `/agents/{agent}/**` is
 * forwarded to that agent's own Cloud Run service with the path unchanged, so a
 * new agent endpoint is reachable with no gateway change. Dev routes through the
 * Vite proxy alias `/agents-api`; prod relies on the Firebase Hosting rewrite for
 * `/agents/**` → platform-gateway.
 *
 * Backs the Configuration → Prompts page (`data-scoring-agent/prompts/**`). Kept
 * as its own client rather than folded into an existing one because `/agents` is a
 * distinct top-level gateway prefix — every Hosting rewrite is path-scoped, so it
 * needs its own entry or the request falls through to the SPA catch-all.
 */
const agentsPrefixUrl = import.meta.env.DEV ? '/agents-api' : '/agents';
/**
 * Content service (`agentic-content-generation-service`) — the control plane over
 * the Temporal content-generation workers, read-backed by ClickHouse. The service
 * serves under the SAME prefix the gateway exposes (`/api/v1/content-service`, its
 * own `api_v1_prefix`), so the gateway forwards the path unchanged — unlike
 * integration-service, which is rewritten. Dev routes through the Vite proxy alias
 * `/content-api`; prod relies on the Firebase Hosting rewrite for
 * `/api/v1/content-service/**` → platform-gateway, which must stay ABOVE the
 * `/api/**` → identity-service catch-all in `firebase.json`.
 */
const contentPrefixUrl = import.meta.env.DEV ? '/content-api' : '/api/v1/content-service';
// ----- catalog-v2 -----
/**
 * Catalog service (`agentic-catalog-service`) — runs, gates, schedules, curation,
 * product master, images and experiments; successor to the Java workflow-service
 * and curation-service. Like content-service it serves under the SAME prefix the
 * gateway exposes (`/api/v1/catalog-service`), so the path is forwarded unchanged.
 * Dev routes through the Vite proxy alias `/catalog-service-api`; prod relies on
 * the Firebase Hosting rewrite for `/api/v1/catalog-service/**` → platform-gateway,
 * which must stay ABOVE the `/api/**` → identity-service catch-all.
 */
const catalogServicePrefixUrl = import.meta.env.DEV
  ? '/catalog-service-api'
  : '/api/v1/catalog-service';
// ----- end catalog-v2 -----

/**
 * Pre-configured Ky instance for the main API (identity service).
 * This is the ONLY place Ky is imported in the app.
 */
export const apiClient = ky.create({
  ...baseKyConfig,
  prefixUrl: apiPrefixUrl,
  hooks: {
    beforeRequest: [bearerInterceptor, subsidiaryInterceptor, localeInterceptor],
    afterResponse: [createAuthInterceptor(() => apiClient)],
    beforeError: [errorInterceptor],
  },
});

/**
 * Pre-configured Ky instance for the integrations API (connections, connectors).
 */
export const integrationsClient = ky.create({
  ...baseKyConfig,
  prefixUrl: integrationsPrefixUrl,
  hooks: {
    beforeRequest: [bearerInterceptor, subsidiaryInterceptor, localeInterceptor],
    afterResponse: [createAuthInterceptor(() => integrationsClient)],
    beforeError: [errorInterceptor],
  },
});

/**
 * Ky instance for the integrations service's PUBLIC routes (`public/**`), used
 * by the `/connector/:token` pages an external Google Ads owner opens without a
 * platform login. Deliberately no bearer / subsidiary / auth interceptors: the
 * link token alone identifies the workspace, and a 401/403 here must render on
 * the page instead of hard-redirecting to `/login`. `credentials: 'include'`
 * carries the httpOnly `connect_session` cookie set by the OAuth callback.
 */
export const publicIntegrationsClient = ky.create({
  ...baseKyConfig,
  prefixUrl: integrationsPrefixUrl,
  credentials: 'include',
  hooks: {
    beforeRequest: [localeInterceptor],
    beforeError: [errorInterceptor],
  },
});

/**
 * Pre-configured Ky instance for the export service (read-only ClickHouse
 * table browser). Same interceptor chain as integrations — platform-gateway
 * validates the bearer token; the service itself is super-admin gated.
 */
export const exportClient = ky.create({
  ...baseKyConfig,
  prefixUrl: exportPrefixUrl,
  hooks: {
    beforeRequest: [bearerInterceptor, subsidiaryInterceptor, localeInterceptor],
    afterResponse: [createAuthInterceptor(() => exportClient)],
    beforeError: [errorInterceptor],
  },
});

/**
 * Pre-configured Ky instance for the experiment service (draft CRUD + PATCH).
 * Same interceptor chain as integrations — platform-gateway validates the
 * bearer token and the `x-subsidiary-id` header on every call.
 */
export const experimentsClient = ky.create({
  ...baseKyConfig,
  prefixUrl: experimentsPrefixUrl,
  hooks: {
    beforeRequest: [bearerInterceptor, subsidiaryInterceptor, localeInterceptor],
    afterResponse: [createAuthInterceptor(() => experimentsClient)],
    beforeError: [errorInterceptor],
  },
});

/**
 * Pre-configured Ky instance for the catalog-operations workflow trigger
 * endpoint (import → enrich → score → deliver). Kept separate from
 * `experimentsClient` and `catalogOpsClient` even though it shares the
 * `/workflow/v1` prefix — a distinct backend resource, not a variant of
 * either existing one.
 */
export const catalogOperationsClient = ky.create({
  ...baseKyConfig,
  prefixUrl: catalogOperationsPrefixUrl,
  hooks: {
    beforeRequest: [bearerInterceptor, subsidiaryInterceptor, localeInterceptor],
    afterResponse: [createAuthInterceptor(() => catalogOperationsClient)],
    beforeError: [errorInterceptor],
  },
});

/**
 * Pre-configured Ky instance for the workflow execution-tracking service
 * (`workflow-webapp`). Same interceptor chain as the other platform-gateway
 * clients — bearer JWT + subsidiary + locale. Kept separate from
 * `catalogOperationsClient` despite the shared `/workflow/v1` prefix because it
 * is a distinct backend service (its own dev proxy target + Firebase rewrite).
 */
export const workflowRunsClient = ky.create({
  ...baseKyConfig,
  prefixUrl: workflowRunsPrefixUrl,
  hooks: {
    beforeRequest: [bearerInterceptor, subsidiaryInterceptor, localeInterceptor],
    afterResponse: [createAuthInterceptor(() => workflowRunsClient)],
    beforeError: [errorInterceptor],
  },
});

/**
 * Pre-configured Ky instance for the config service (KPI catalogue,
 * content-attribute taxonomy). Shares the temporary API-key requirement
 * with the experiment service.
 */
export const configClient = ky.create({
  ...baseKyConfig,
  prefixUrl: configPrefixUrl,
  hooks: {
    beforeRequest: [bearerInterceptor, subsidiaryInterceptor, localeInterceptor],
    afterResponse: [createAuthInterceptor(() => configClient)],
    beforeError: [errorInterceptor],
  },
});

/**
 * Pre-configured Ky instance for the catalog-operations / enrichments service
 * (jobs listing, job detail, jobs/summary). Standard bearer + subsidiary +
 * locale interceptors plus the temporary platform-gateway X-API-Key (same
 * shared key used by experimentsClient / configClient — drop together).
 */
export const catalogOpsClient = ky.create({
  ...baseKyConfig,
  prefixUrl: catalogOpsPrefixUrl,
  hooks: {
    beforeRequest: [bearerInterceptor, subsidiaryInterceptor, localeInterceptor],
    afterResponse: [createAuthInterceptor(() => catalogOpsClient)],
    beforeError: [errorInterceptor],
  },
});

/**
 * Pre-configured Ky instance for the curation service. Backs the per-batch
 * product listing on the catalog-operations products page. Same interceptor
 * chain as catalogOpsClient — bearer + subsidiary + locale + temp X-API-Key.
 */
export const curationClient = ky.create({
  ...baseKyConfig,
  prefixUrl: curationPrefixUrl,
  hooks: {
    beforeRequest: [bearerInterceptor, subsidiaryInterceptor, localeInterceptor],
    afterResponse: [createAuthInterceptor(() => curationClient)],
    beforeError: [errorInterceptor],
  },
});

/**
 * Pre-configured Ky instance for the notification service (via platform-gateway).
 * Same auth chain as integrations — bearer JWT + tenant header + locale.
 */
export const notificationsClient = ky.create({
  ...baseKyConfig,
  prefixUrl: notificationsPrefixUrl,
  hooks: {
    beforeRequest: [bearerInterceptor, subsidiaryInterceptor, localeInterceptor],
    afterResponse: [createAuthInterceptor(() => notificationsClient)],
    beforeError: [errorInterceptor],
  },
});

/**
 * Pre-configured Ky instance for the analytics service (experiment dashboards).
 * Standard bearer + subsidiary + locale chain only — `analytics_routes.py`
 * on the gateway has no `Depends(require_api_key)` (intentional: analytics
 * is gated by Cloud Run IAM at the proxy layer), so adding
 * `platformGatewayApiKeyInterceptor` would just send an unused header.
 */
export const analyticsClient = ky.create({
  ...baseKyConfig,
  prefixUrl: analyticsPrefixUrl,
  hooks: {
    beforeRequest: [bearerInterceptor, subsidiaryInterceptor, localeInterceptor],
    afterResponse: [createAuthInterceptor(() => analyticsClient)],
    beforeError: [errorInterceptor],
  },
});

/**
 * Pre-configured Ky instance for the agent APIs behind platform-gateway's
 * `/agents/{agent}/**` proxy. Same interceptor chain as the other gateway
 * clients — bearer JWT + subsidiary + locale, and nothing else: the agent
 * routes it serves are authenticated with the user's own token, so there is no
 * API key and no separate secret to attach here.
 */
export const agentsClient = ky.create({
  ...baseKyConfig,
  prefixUrl: agentsPrefixUrl,
  hooks: {
    beforeRequest: [bearerInterceptor, subsidiaryInterceptor, localeInterceptor],
    afterResponse: [createAuthInterceptor(() => agentsClient)],
    beforeError: [errorInterceptor],
  },
});

/**
 * Pre-configured Ky instance for the content service (via platform-gateway).
 *
 * Bearer + subsidiary + locale only. The service requires BOTH a user JWT and
 * `X-Subsidiary-ID` on every business route and cross-checks the two — a mismatch
 * is a 403, not a silent cross-tenant read. No API-key interceptor: the gateway's
 * `content_service_routes.py` carries no `require_api_key` dependency (same as
 * analytics), so the header would only ever be ignored.
 */
export const contentClient = ky.create({
  ...baseKyConfig,
  prefixUrl: contentPrefixUrl,
  hooks: {
    beforeRequest: [bearerInterceptor, subsidiaryInterceptor, localeInterceptor],
    afterResponse: [createAuthInterceptor(() => contentClient)],
    beforeError: [errorInterceptor],
  },
});

// ----- catalog-v2 -----
/**
 * Pre-configured Ky instance for agentic-catalog-service (via platform-gateway).
 * Standard bearer + subsidiary + locale chain; the new catalog screens use it in
 * parallel with the legacy catalog clients until the cutover retires them.
 */
export const catalogServiceClient = ky.create({
  ...baseKyConfig,
  prefixUrl: catalogServicePrefixUrl,
  hooks: {
    beforeRequest: [bearerInterceptor, subsidiaryInterceptor, localeInterceptor],
    afterResponse: [createAuthInterceptor(() => catalogServiceClient)],
    beforeError: [errorInterceptor],
  },
});
// ----- end catalog-v2 -----

/**
 * Pre-configured Ky instance for the ai-mcp-server (chat / copilot SSE).
 * Separate Cloud Run service from platform-gateway, so it can't share the
 * `/api` prefix. `prefixUrl` is sourced from `mcpApiBaseUrl`:
 *   - Dev:  Vite proxy alias `/mcp-api` → `VITE_MCP_BASE_URL`
 *   - Prod: absolute Cloud Run URL (`VITE_MCP_BASE_URL`) — bypasses Firebase
 *           Hosting because the Hosting CDN buffers `text/event-stream`.
 * The standard bearer/subsidiary/locale chain is included so tenant context
 * flows through identically to the platform-gateway clients, even though the
 * MCP server treats those headers as advisory today.
 */
export const mcpClient = ky.create({
  ...baseKyConfig,
  prefixUrl: mcpApiBaseUrl,
  hooks: {
    beforeRequest: [bearerInterceptor, subsidiaryInterceptor, localeInterceptor],
    afterResponse: [createAuthInterceptor(() => mcpClient)],
    beforeError: [errorInterceptor],
  },
});

/**
 * Gateway-absolute path → the Ky client that serves it. Some APIs (e.g. the
 * Batch Details Funnel's `productsPath`, `actions.*Path`, `links.*`) hand back
 * paths that already carry the platform-gateway prefix. Routing them through
 * the matching client keeps the dev proxy rewrite and the auth / subsidiary /
 * locale interceptors, instead of rebuilding the URL by hand.
 */
const GATEWAY_ROUTES = [
  { prefix: '/workflow/v1/', client: () => catalogOperationsClient },
  { prefix: '/curation/', client: () => curationClient },
  { prefix: '/api/v1/integration/', client: () => integrationsClient },
] as const;

/** Options for {@link gatewayRequest}. `searchParams` are merged over any
 *  query string the path already carries; nullish / empty values are dropped. */
export interface GatewayRequestOptions {
  method?: 'get' | 'post';
  searchParams?: Record<string, string | number | boolean | null | undefined>;
  json?: unknown;
}

/** True when {@link gatewayRequest} can route `path` (a known gateway prefix). */
export function isGatewayPath(path: string | null | undefined): path is string {
  return !!path && GATEWAY_ROUTES.some((r) => path.startsWith(r.prefix));
}

/**
 * Calls a server-provided gateway-absolute path (e.g. `/curation/runs/{id}/products?reason_code=X`)
 * on the client that owns its prefix. Throws for an unknown prefix rather than
 * silently hitting the identity service.
 */
export function gatewayRequest(
  path: string,
  { method = 'get', searchParams, json }: GatewayRequestOptions = {},
): ResponsePromise {
  const route = GATEWAY_ROUTES.find((r) => path.startsWith(r.prefix));
  if (!route) throw new Error(`Unsupported gateway path: ${path}`);
  const relative = path.slice(route.prefix.length);
  const queryIndex = relative.indexOf('?');
  const input = queryIndex === -1 ? relative : relative.slice(0, queryIndex);
  const params = new URLSearchParams(queryIndex === -1 ? '' : relative.slice(queryIndex + 1));
  for (const [key, value] of Object.entries(searchParams ?? {})) {
    if (value !== undefined && value !== null && value !== '') params.set(key, String(value));
  }
  return route.client()(input, {
    method,
    searchParams: params,
    ...(json === undefined ? {} : { json }),
  });
}
